import type { DocPage } from './types';

/**
 * One page per feature, each carrying the status that feeds the audit checklist.
 *
 * The statuses were read off the source, not off the roadmap. Where a feature is
 * marked `partial` the `gaps` array names exactly what is unfinished, so the
 * checklist page can show the same thing without anyone keeping two lists in
 * sync by hand.
 */
export const FEATURE_PAGES: DocPage[] = [
  // -------------------------------------------------------------------------
  {
    slug: 'approval-queue',
    title: 'Approval queue',
    summary: 'The cross-client list of everything waiting on a decision, with inline approve and decline.',
    section: 'features',
    keywords: ['queue', 'approve', 'decline', 'pending', 'waiting', 'decision'],
    status: {
      state: 'shipped',
      frontend: 'yes',
      backend: 'yes',
      persistence: 'yes',
      auth: 'yes',
      permissions: 'yes',
      errorHandling: 'yes',
      tests: 'yes',
    },
    blocks: [
      { kind: 'heading', body: 'Purpose' },
      {
        kind: 'text',
        body: 'One board covering every client, so an agency owner can clear the day’s decisions in a single sitting rather than visiting each client in turn.',
      },
      { kind: 'heading', body: 'Who can use it' },
      { kind: 'text', body: 'agency_owner and agency_member. Clients use the portal instead.' },
      { kind: 'heading', body: 'Steps' },
      {
        kind: 'steps',
        items: [
          'Open Agency. The queue is the first panel, headed "Waiting on you".',
          'Read the plain-language summary. It never shows a tool name or an endpoint.',
          'Approve, or decline.',
        ],
      },
      { kind: 'screenshot', caption: 'Agency dashboard, cross-client queue with two pending actions' },
      { kind: 'heading', body: 'What you get back' },
      {
        kind: 'text',
        body: 'On approve the action executes immediately through the connector and the waiting workflow receives the result. On decline the request becomes terminal and nothing runs.',
      },
      {
        kind: 'note',
        tone: 'info',
        body: 'Rows marked "client decides" cannot be actioned by you. They are shown so you know what your client is sitting on, not so you can clear it for them.',
      },
      { kind: 'heading', body: 'Common errors' },
      {
        kind: 'errors',
        rows: [
          {
            code: 'approval_not_pending',
            cause: 'Somebody already decided it, or it expired while the page was open.',
            fix: 'Refresh. The decision that landed first stands.',
          },
          {
            code: 'approver_role_not_permitted',
            cause: 'Policy routed this action to the client and you tried to approve it.',
            fix: 'Ask the client to approve it in their portal, or change the workspace policy for future actions.',
          },
          {
            code: 'approval_terminal',
            cause: 'The request already reached a final state.',
            fix: 'Nothing to do. Terminal states never change.',
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    slug: 'approval-policy',
    title: 'Approval policy',
    summary: 'Per-client rules deciding what runs automatically, what you sign, and what only the client can sign.',
    section: 'features',
    keywords: ['policy', 'rules', 'routing', 'money', 'bulk', 'irreversible', 'threshold'],
    status: {
      state: 'shipped',
      frontend: 'yes',
      backend: 'yes',
      persistence: 'yes',
      auth: 'yes',
      permissions: 'yes',
      errorHandling: 'yes',
      tests: 'yes',
    },
    blocks: [
      { kind: 'heading', body: 'Purpose' },
      {
        kind: 'text',
        body: 'Decides who has to sign for each class of action, per client. This is the single most consequential setting in the product.',
      },
      { kind: 'heading', body: 'Who can use it' },
      { kind: 'text', body: 'agency_owner. Clients cannot change their own policy.' },
      { kind: 'heading', body: 'Steps' },
      {
        kind: 'steps',
        items: [
          'Agency → Clients → Settings on the client.',
          'Set each of the five lanes: reading data, making changes, moving money, bulk actions, irreversible actions.',
          'Adjust the thresholds: what counts as bulk, how long an approval stays open, and the money figure above which the client always signs.',
          'Add per-tool exceptions for named operations such as stripe.refunds.create.',
          'Save.',
        ],
      },
      { kind: 'heading', body: 'The four decisions' },
      {
        kind: 'table',
        headers: ['Setting', 'Effect'],
        rows: [
          ['Run automatically', 'Executes on arrival. Still fully logged.'],
          ['You approve', 'Someone at your agency signs off.'],
          ['Client approves', 'Only the client can sign. Your agency cannot, ever.'],
          ['Never allow', 'Rejected the moment it arrives.'],
        ],
      },
      { kind: 'screenshot', caption: 'Workspace editor, the "Who approves what" panel' },
      {
        kind: 'note',
        tone: 'warn',
        body: 'The money threshold can only raise the bar, never lower it. Setting money to "run automatically" with a threshold of $100 still escalates anything at or above $100 to the client.',
      },
      { kind: 'heading', body: 'Inputs' },
      {
        kind: 'list',
        items: [
          'Bulk threshold: records touched before an action counts as bulk. Minimum 2.',
          'Expiry: hours a pending approval stays open before it is declined automatically. Minimum 1.',
          'Client approval money threshold: an amount, or blank for no additional threshold.',
        ],
      },
      {
        kind: 'note',
        tone: 'info',
        body: 'An expiry is always a safe reject. An unattended action fails closed — it never becomes an approval that anyone can win by waiting.',
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    slug: 'client-portal',
    title: 'Client portal',
    summary: 'What your client sees: this week in plain language, anything needing them, and searchable history.',
    section: 'features',
    keywords: ['portal', 'client', 'magic link', 'history', 'signin', 'customer'],
    status: {
      state: 'partial',
      frontend: 'yes',
      backend: 'yes',
      persistence: 'yes',
      auth: 'yes',
      permissions: 'yes',
      errorHandling: 'yes',
      tests: 'yes',
      gaps: [
        'Client logins require the Agency plan. Upgrading works, but payment verification is currently off — see Billing & plans.',
        'Invites need RESEND_API_KEY configured on the server, otherwise no email is sent and the invite reports failure.',
      ],
    },
    blocks: [
      { kind: 'heading', body: 'Purpose' },
      {
        kind: 'text',
        body: 'Gives a non-technical client a ten-second answer to "what did my agents do, and does anything need me?" — without a password, an install, or a single tool name.',
      },
      { kind: 'heading', body: 'Who can use it' },
      { kind: 'text', body: 'client_approver decides and reads. client_viewer reads only.' },
      { kind: 'heading', body: 'How a client gets in' },
      {
        kind: 'steps',
        items: [
          'You send an invite from Agency → Clients → Invite client.',
          'They receive a single-use link, valid for one hour.',
          'Clicking it creates a session lasting two weeks, scoped to their workspace alone.',
        ],
      },
      {
        kind: 'note',
        tone: 'danger',
        body: 'The link is never shown to you. It redeems into a client_approver session, so holding it would let your agency sign as the client. It goes to their address or nowhere.',
      },
      { kind: 'heading', body: 'What they see' },
      {
        kind: 'list',
        items: [
          'A headline sentence covering the week, and money moved if any.',
          'Cards for anything needing their approval, each with the consequence of getting it wrong.',
          'Recent activity, then searchable history with filters for date range, tool, risk and outcome.',
        ],
      },
      { kind: 'screenshot', caption: 'Client portal with one pending approval and the history filters open' },
      {
        kind: 'text',
        body: 'History is paged. How far back it goes depends on your plan’s retention window; the portal says so when older entries are being hidden.',
      },
      { kind: 'heading', body: 'Common errors' },
      {
        kind: 'errors',
        rows: [
          {
            code: 'invalid_link',
            cause: 'The link was already used, or more than an hour has passed.',
            fix: 'Ask the agency for a new one. Links are deliberately single-use.',
          },
          {
            code: 'session_expired',
            cause: 'The two-week session has ended.',
            fix: 'Request a fresh sign-in link.',
          },
          {
            code: 'viewer_read_only',
            cause: 'A client_viewer tried to approve something.',
            fix: 'The agency issues an approver link instead of a viewer link.',
          },
          {
            code: 'client_logins_not_included',
            cause: 'The agency is on the Developer plan, which has no client portal logins.',
            fix: 'Upgrade to Agency. Not currently possible in-product — see Billing & plans.',
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    slug: 'audit-log',
    title: 'Audit log',
    summary: 'An append-only, hash-chained record per client that detects both edits and deletions.',
    section: 'features',
    keywords: ['audit', 'log', 'chain', 'tamper', 'compliance', 'history', 'verify'],
    status: {
      state: 'shipped',
      frontend: 'partial',
      backend: 'yes',
      persistence: 'yes',
      auth: 'yes',
      permissions: 'yes',
      errorHandling: 'yes',
      tests: 'yes',
      gaps: [
        'GET /api/approvals/audit returns rows and a verification result, but no agency screen renders them — the log is API-only today.',
      ],
    },
    blocks: [
      { kind: 'heading', body: 'Purpose' },
      {
        kind: 'text',
        body: 'Proves what happened. Every request, routing decision, approval, execution and failure is appended with the hash of the entry before it, so the record can be replayed and checked by anyone holding the rows.',
      },
      { kind: 'heading', body: 'What it catches' },
      {
        kind: 'list',
        items: [
          'Editing a past entry — its content hash stops matching, and every link after it breaks.',
          'Deleting the tail — the per-workspace head record still names a later sequence number than the last surviving entry.',
          'Two processes appending at once — sequence numbers are allocated inside a transaction, so the chain cannot fork.',
        ],
      },
      {
        kind: 'note',
        tone: 'info',
        body: 'A forked chain was possible before the store became durable: a second server instance would restart at sequence 0 and both halves would verify as internally consistent. That is what the head record and the transactional append exist to prevent.',
      },
      { kind: 'heading', body: 'Reading it' },
      {
        kind: 'code',
        language: 'bash',
        body: `curl "https://your-api-host/api/approvals/audit?workspaceId=<id>" \\
  -H "Authorization: Bearer <firebase id token>"`,
      },
      {
        kind: 'text',
        body: 'The response carries the entries and a verification result in the same payload, so a consumer never has to trust the rows without being able to check them.',
      },
      {
        kind: 'todo',
        body: 'Build an agency-side audit viewer. The endpoint, the filters and the chain verification all exist; nothing in the web app calls them.',
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    slug: 'gateway-keys',
    title: 'Approval gateway keys',
    summary: 'The API credential your automations authenticate with, scoped to your agency alone.',
    section: 'features',
    keywords: ['api key', 'token', 'gateway', 'credential', 'revoke', 'secret'],
    status: {
      state: 'shipped',
      frontend: 'yes',
      backend: 'yes',
      persistence: 'yes',
      auth: 'yes',
      permissions: 'yes',
      errorHandling: 'yes',
      tests: 'yes',
    },
    blocks: [
      { kind: 'heading', body: 'Purpose' },
      {
        kind: 'text',
        body: 'Workflow platforms cannot hold a browser session, so the public gateway is keyed instead. A key identifies one agency, and that identity is what scopes every request behind it.',
      },
      { kind: 'heading', body: 'Who can use it' },
      { kind: 'text', body: 'agency_owner.' },
      { kind: 'heading', body: 'Steps' },
      {
        kind: 'steps',
        items: [
          'Agency → Approval gateway.',
          'Name the key after where it will live, so you can tell your integrations apart later.',
          'Create, then copy the token immediately.',
          'Paste it into n8n, Make, or your environment as CHAIRLY_API_KEY.',
        ],
      },
      {
        kind: 'note',
        tone: 'danger',
        body: 'Only a hash is stored. Nobody, including us, can recover the token after the panel closes. If you lose it, revoke the key and create another.',
      },
      { kind: 'heading', body: 'Scope' },
      {
        kind: 'list',
        items: [
          'A key can only submit actions into workspaces its own agency owns.',
          'It can only read approvals belonging to that agency. Another agency’s approval id returns 404.',
          'Revocation is immediate and permanent. The key stays visible in the list, marked revoked, so you can see it existed.',
        ],
      },
      { kind: 'screenshot', caption: 'Approval gateway panel with an active key and a revoked one' },
      { kind: 'heading', body: 'Common errors' },
      {
        kind: 'errors',
        rows: [
          {
            code: 'unauthorized',
            cause: 'Missing, unknown or revoked x-chairly-key header.',
            fix: 'Check the header name and that the key has not been revoked. Create a new one if unsure.',
          },
          {
            code: 'client_workspace_not_found',
            cause: 'The workspaceId is not one of yours — or does not exist. The two are deliberately indistinguishable.',
            fix: 'Confirm the workspace id belongs to your agency.',
          },
          {
            code: 'gateway_unavailable',
            cause: 'The key could not be verified because the database was unreachable.',
            fix: 'Retry shortly. An unverifiable key is never treated as valid.',
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    slug: 'white-label',
    title: 'White label',
    summary: 'Put your own name, colours and domain on the client-facing portal and emails.',
    section: 'features',
    keywords: ['branding', 'white label', 'domain', 'logo', 'colours', 'subdomain', 'cname'],
    status: {
      state: 'partial',
      frontend: 'yes',
      backend: 'yes',
      persistence: 'yes',
      auth: 'yes',
      permissions: 'yes',
      errorHandling: 'yes',
      tests: 'yes',
      gaps: [
        'Custom domain verification calls Cloudflare and needs CLOUDFLARE_API_TOKEN and CLOUDFLARE_ZONE_ID. Without them a domain stays at pending_dns and is never served.',
        'Higher levels need a higher plan. Upgrading works, but activates without payment while verification is off — see Billing & plans.',
      ],
    },
    blocks: [
      { kind: 'heading', body: 'Purpose' },
      {
        kind: 'text',
        body: 'Your clients should see your agency, not ours. How far that goes is set by your plan.',
      },
      {
        kind: 'table',
        headers: ['Level', 'Plan', 'What it covers'],
        rows: [
          ['0', 'Developer', 'None. Chairly branding throughout.'],
          ['1', 'Startup', 'Your name, logo, colours, and a yourname.chairly.app subdomain.'],
          ['2', 'Agency', 'Everything above, plus your own domain via CNAME with SSL issued for you.'],
          ['3', 'Business', 'Everything above, plus your own email sending domain and removal of "Powered by Chairly".'],
        ],
      },
      {
        kind: 'note',
        tone: 'info',
        body: 'The level is derived from your plan and never stored. Downgrading stops the higher chrome from rendering immediately, but your settings are kept — upgrading again restores them exactly as they were.',
      },
      { kind: 'heading', body: 'Setting a custom domain' },
      {
        kind: 'steps',
        items: [
          'Agency → White label → Custom domain. Enter the hostname, for example portal.youragency.com.',
          'Save. The DNS records you need appear underneath.',
          'Add them with your DNS provider.',
          'Press "Check DNS now". Repeat until the status reads active.',
        ],
      },
      { kind: 'screenshot', caption: 'White label panel showing DNS records and the check button' },
      {
        kind: 'note',
        tone: 'warn',
        body: 'Your clients keep using the Chairly subdomain until the status reaches active. A claimed but unverified domain is never served — it would send them to a hostname with no certificate.',
      },
      { kind: 'heading', body: 'Common errors' },
      {
        kind: 'errors',
        rows: [
          {
            code: 'branding_level_required',
            cause: 'A field above your plan’s level was submitted.',
            fix: 'Upgrade, or leave that field alone. Locked fields are shown greyed with the plan that unlocks them.',
          },
          {
            code: 'no_custom_domain',
            cause: 'A verification was requested before any domain was claimed.',
            fix: 'Save a hostname first.',
          },
        ],
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    slug: 'monthly-report',
    title: 'Monthly value report',
    summary: 'A branded, print-ready summary of what your agents did for one client in a month.',
    section: 'features',
    keywords: ['report', 'monthly', 'value', 'pdf', 'export', 'email'],
    status: {
      state: 'partial',
      frontend: 'yes',
      backend: 'yes',
      persistence: 'yes',
      auth: 'yes',
      permissions: 'yes',
      errorHandling: 'yes',
      tests: 'partial',
      gaps: [
        'Delivery goes to the agency’s support email, not to the client. Forwarding is manual.',
        'The email only fires if the agency has a support email set and RESEND_API_KEY is configured.',
        'PDF export is the browser’s print-to-PDF. There is no server-side renderer.',
      ],
    },
    blocks: [
      { kind: 'heading', body: 'Purpose' },
      {
        kind: 'text',
        body: 'The artefact you put in front of a client to justify the retainer: what ran, what it moved, how fast decisions came back, and what went wrong.',
      },
      { kind: 'heading', body: 'Viewing one now' },
      {
        kind: 'steps',
        items: [
          'Agency → Clients → Monthly report on the client.',
          'It opens in a new tab as a print-ready document.',
          'Print to PDF if you need to attach it to something.',
        ],
      },
      { kind: 'heading', body: 'Automatic delivery' },
      {
        kind: 'text',
        body: 'On the 1st of each month the server emails the previous month’s report, one per client workspace, to your agency’s support email address.',
      },
      {
        kind: 'note',
        tone: 'warn',
        body: 'If no support email is set the run is skipped for that agency with a warning in the server log. It is not sent anywhere else — a display name is not an address.',
      },
      {
        kind: 'todo',
        body: 'Send the report to the client contact directly, with the agency on reply-to. Today it reaches the agency and has to be forwarded by hand.',
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    slug: 'weekly-review',
    title: 'Weekly review brief',
    summary: 'The week’s facts for one client, written as sentences to read out on a call.',
    section: 'features',
    keywords: ['weekly', 'review', 'brief', 'meeting', 'voice', 'call'],
    status: {
      state: 'partial',
      frontend: 'yes',
      backend: 'yes',
      persistence: 'yes',
      auth: 'yes',
      permissions: 'yes',
      errorHandling: 'yes',
      tests: 'no',
      gaps: [
        'Designed to open the voice review room with the week’s record. Nothing under services/meeting or services/voice imports it — the brief is read in the dashboard only.',
        'No automated tests cover buildWeeklyReviewBrief.',
      ],
    },
    blocks: [
      { kind: 'heading', body: 'Purpose' },
      {
        kind: 'text',
        body: 'Everything worth saying about one client’s week, in the order you would say it: what ran, what needs raising, what is still waiting.',
      },
      { kind: 'heading', body: 'Steps' },
      {
        kind: 'steps',
        items: [
          'Agency → Clients → This week on the client.',
          'The opening lines are deterministic and safe to read verbatim.',
          'Below them: what ran, failures to raise, and anything still pending.',
        ],
      },
      { kind: 'screenshot', caption: 'Weekly review drawer with opening lines and the week’s counts' },
      {
        kind: 'note',
        tone: 'info',
        body: 'Pending items marked "client decides" are shown so you can chase them on the call. You still cannot approve them yourself.',
      },
      {
        kind: 'todo',
        body: 'Feed the brief into the voice meeting room so the chair opens a review already knowing the week. The brief was built for this and the wiring was never done.',
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    slug: 'billing',
    title: 'Billing & plans',
    summary: 'What each plan includes, how overage works, and the parts that are not connected yet.',
    section: 'features',
    keywords: ['billing', 'plans', 'pricing', 'upgrade', 'subscription', 'overage', 'stripe'],
    status: {
      state: 'partial',
      frontend: 'yes',
      backend: 'yes',
      persistence: 'yes',
      auth: 'yes',
      permissions: 'yes',
      errorHandling: 'yes',
      tests: 'yes',
      gaps: [
        'Payment verification is deliberately switched off while the rest of the platform is built. With BILLING_MODE=unverified, selecting a plan activates it with no charge and metering does not run.',
        'Stripe metering needs STRIPE_PRICE_OVERAGE and a metered item on the subscription. Without either, overage falls back to the manual invoicing queue.',
        'Lemon Squeezy has no usage-record API, so its overage always needs invoicing by hand. That queue has no dedicated screen — it is visible per agency, not across the platform.',
      ],
    },
    blocks: [
      {
        kind: 'note',
        tone: 'danger',
        body: 'Payment verification is currently switched off. On a deployment running BILLING_MODE=unverified, choosing a plan activates it immediately without taking any money. This is a deliberate stand-in while the rest of the platform is finished — every plan granted this way is recorded with planActivation.mode === "unverified" so the accounts can be reconciled when payment is turned on.',
      },
      { kind: 'heading', body: 'The plans' },
      {
        kind: 'table',
        headers: ['Plan', 'Price', 'Clients', 'Approvals / month', 'White label', 'Client logins'],
        rows: [
          ['Developer', 'Free', '1', '100', 'Level 0', 'No'],
          ['Startup', '$99', '3', '5,000', 'Level 1', 'No'],
          ['Agency', '$299', '10', '25,000', 'Level 2', 'Yes'],
          ['Business', '$799', '30', '100,000', 'Level 3', 'Yes'],
        ],
      },
      { kind: 'heading', body: 'What is enforced today' },
      {
        kind: 'list',
        items: [
          'The client workspace cap. Counted inside a transaction, so two simultaneous creates cannot both slip past it.',
          'The white-label level, derived from the plan on every render.',
          'Client portal logins, refused with a 402 on the Developer plan.',
          'Log retention in the portal’s history view.',
        ],
      },
      { kind: 'heading', body: 'Overage' },
      {
        kind: 'text',
        body: 'Approvals beyond the monthly allowance are recorded against the agency and the period. Overage bills but never blocks — a client’s action is never refused because of your billing state.',
      },
      { kind: 'heading', body: 'How overage is charged' },
      {
        kind: 'text',
        body: 'A daily sweep recomputes each paid agency’s overage for the current period and reports it to that agency’s processor. The figure is absolute rather than incremental, so running the sweep twice converges instead of billing the same approvals again.',
      },
      {
        kind: 'table',
        headers: ['Processor', 'What happens'],
        rows: [
          ['Stripe', 'A usage record is set on the metered subscription item, and it lands on the next invoice.'],
          ['Lemon Squeezy', 'It has no usage API, so the overage is flagged for manual invoicing and appears in your billing panel.'],
        ],
      },
      {
        kind: 'note',
        tone: 'info',
        body: 'The overage is written to the database before the processor is contacted. A processor being unreachable loses the automation for that run, never the money — the row is already there, marked for a human.',
      },
      { kind: 'heading', body: 'Cancelling and changing plan' },
      {
        kind: 'steps',
        items: [
          'Agency → Plan & billing.',
          'Change plan opens Pricing; choosing a different plan moves you there.',
          'Cancel plan ends the subscription at the end of the period you have already paid for.',
          'Cards & invoices opens the processor’s own page.',
        ],
      },
      {
        kind: 'note',
        tone: 'warn',
        body: 'Cancelling never cuts capacity mid-period. An agency that paid for the month keeps its clients running for the month — stopping immediately would strand the workflows this product exists to protect. On a deployment with payment verification off there is nothing to schedule, so the plan drops to Developer straight away and the UI says so.',
      },
      { kind: 'heading', body: 'Upgrading' },
      {
        kind: 'steps',
        items: [
          'Open Pricing, from the upgrade prompt on the dashboard or directly.',
          'Choose monthly or yearly. Yearly is 20% cheaper.',
          'Press Upgrade on the plan you want.',
        ],
      },
      {
        kind: 'text',
        body: 'What happens next depends on the deployment. With payment verification on, you are sent to the processor’s hosted checkout and the plan changes only once the webhook confirms the charge. With it off, the plan is active immediately and the page tells you no payment was taken.',
      },
      {
        kind: 'todo',
        body: 'Turn payment verification on by unsetting BILLING_MODE, then reconcile every agency whose planActivation.mode is "unverified".',
      },
    ],
  },

  // -------------------------------------------------------------------------
  {
    slug: 'voice-room',
    title: 'Voice meeting room',
    summary: 'The original product surface: a spoken meeting with AI seats. Retained, frozen, not part of the control plane.',
    section: 'features',
    keywords: ['voice', 'meeting', 'seats', 'room', 'chair', 'legacy'],
    status: {
      state: 'partial',
      frontend: 'yes',
      backend: 'yes',
      persistence: 'yes',
      auth: 'yes',
      permissions: 'partial',
      errorHandling: 'partial',
      tests: 'partial',
      gaps: [
        'Frozen surface. It predates the agency pivot and is not covered by the approval control plane except through the voice-decision endpoint.',
        'The "demo mode" badge for a seat running on the synthetic fallback appears on the dashboard seats tab only, not on the /seats setup page where the spec placed it.',
        'Billing for this surface still uses the legacy per-user tiers rather than agency plans.',
      ],
    },
    blocks: [
      {
        kind: 'note',
        tone: 'info',
        body: 'This is the product Chairly began as. It still works and is still billed on the old tiers, but it is not where the agency control plane lives. Treat it as a separate surface.',
      },
      { kind: 'heading', body: 'What it does' },
      {
        kind: 'list',
        items: [
          'A spoken meeting with several AI seats — finance, marketing, ops, sales — chaired around a business profile.',
          'Seats can be backed by a real external agent over a webhook, or fall back to a synthetic response.',
          'Approvals can be cleared by voice during a review, through the same policy gate as every other surface.',
        ],
      },
      {
        kind: 'note',
        tone: 'warn',
        body: 'A spoken "approve it" is not a shortcut. It runs through the same decideApproval gate, so policy still decides whether that role may sign that action.',
      },
      {
        kind: 'todo',
        body: 'Move the demo-mode badge onto the /seats setup page, where an operator configuring a seat would actually see that it has no webhook and is answering synthetically.',
      },
    ],
  },
];
